"use client";

import { useMemo, useRef, useState, useEffect } from "react";
import { MessageCircle, X, Users, Maximize2, Minimize2, Search, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Loader2, Send } from "lucide-react";
import ChatDockLauncher from "@/components/chat/ChatDockLauncher";
import { useAgentChat } from "@/hooks/useAgentChat";

const AgentChatDock = () => {
  const [isOpen, setIsOpen] = useState(false);
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    user,
    agents,
    filteredAgents,
    isLoadingAgents,
    agentsError,
    selectedAgentId,
    setSelectedAgentId,
    messages,
    isLoadingMessages,
    messagesError,
    messageInput,
    setMessageInput,
    isSending,
    searchTerm,
    setSearchTerm,
    handleSendMessage,
  } = useAgentChat();

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) || null,
    [agents, selectedAgentId]
  );

  useEffect(() => {
    if (isOpen && messageEndRef.current) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [isOpen, messages, selectedAgentId]);

  if (!user) return null;

  const renderAgent = (agent: typeof agents[number]) => {
    const lastSeenText = agent.lastActivityAt
      ? formatDistanceToNow(new Date(agent.lastActivityAt), { addSuffix: true })
      : "No activity";

    return (
      <button
        key={agent.id}
        onClick={() => setSelectedAgentId(agent.id)}
        className={cn(
          "w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-all duration-200 text-sm group",
          "hover:shadow-md hover:scale-[1.02] active:scale-[0.98]",
          selectedAgentId === agent.id
            ? "border-[#9bc53d] bg-gradient-to-r from-[#f3fadf] to-[#f3fadf]/80 shadow-sm"
            : "border-border/50 bg-card hover:border-[#9bc53d]/30 hover:bg-[#f3fadf]/50"
        )}
      >
        <div className="relative">
          <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm">
            <AvatarFallback className={cn(
              "text-xs font-semibold transition-colors",
              selectedAgentId === agent.id 
                ? "bg-gradient-to-br from-[#9bc53d] to-[#9bc53d]/80 text-white" 
                : "bg-gradient-to-br from-muted to-muted/80 text-muted-foreground"
            )}>
              {agent.name
                .split(" ")
                .map((chunk) => chunk[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {agent.isOnline && (
            <span className="absolute bottom-0 right-0 h-3 w-3 bg-emerald-500 border-2 border-background rounded-full shadow-sm animate-pulse" />
          )}
        </div>
        <div className="flex-1 overflow-hidden min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate text-foreground">{agent.name}</p>
            {agent.unreadCount > 0 && (
              <Badge className="bg-[#9bc53d] text-white px-1.5 py-0 text-[10px] font-bold shadow-sm animate-in zoom-in-50">
                {agent.unreadCount > 99 ? "99+" : agent.unreadCount}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{agent.email}</p>
          <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className={cn(
              "h-2 w-2 rounded-full transition-all",
              agent.isOnline ? "bg-emerald-500 shadow-sm shadow-emerald-500/50" : "bg-gray-400"
            )} />
            <span className="truncate">{agent.isOnline ? "Active now" : lastSeenText}</span>
          </div>
        </div>
      </button>
    );
  };

  const renderMessages = () => {
    if (!selectedAgent) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground">
          <div className="rounded-full bg-muted p-4">
            <Users className="h-8 w-8 opacity-60" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Select an agent to start chatting</p>
            <p className="text-xs">Choose from the list on the left</p>
          </div>
        </div>
      );
    }

    if (messagesError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-destructive p-4">
          <p className="text-sm font-medium">{messagesError}</p>
          <p className="text-xs text-muted-foreground">Please try again later</p>
        </div>
      );
    }

    if (isLoadingMessages) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground">Loading messages...</p>
          </div>
        </div>
      );
    }

    if (!messages.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground p-6">
                  <div className="rounded-full bg-gradient-to-br from-[#f3fadf] to-[#f3fadf]/80 p-4">
            <MessageCircle className="h-8 w-8 text-[#9bc53d] opacity-60" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs">Say hi to {selectedAgent.name.split(" ")[0]}! 👋</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 text-sm px-1">
        {messages.map((msg, index) => {
          const isOwn = msg.sender_id === Number(user.id);
          const showTimestamp = index === 0 || 
            new Date(msg.created_at).getTime() - new Date(messages[index - 1].created_at).getTime() > 300000; // 5 minutes
          
          return (
            <div key={msg.id} className="space-y-1">
              {showTimestamp && (
                <div className="flex justify-center py-2">
                  <span className="text-[10px] text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                    {new Date(msg.created_at).toLocaleDateString()} {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              <div className={cn("flex w-full gap-2", isOwn ? "justify-end" : "justify-start")}>
                {!isOwn && (
                  <Avatar className="h-6 w-6 mt-1 flex-shrink-0">
                    <AvatarFallback className="bg-[#f3fadf] text-[#9bc53d] text-[10px]">
                      {selectedAgent.name[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className={cn("flex flex-col max-w-[75%]", isOwn ? "items-end" : "items-start")}>
                  {!isOwn && (
                    <span className="text-[10px] text-muted-foreground mb-1 px-2">
                      {selectedAgent.name.split(" ")[0]}
                    </span>
                  )}
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-2.5 text-sm shadow-sm transition-all duration-200",
                      "break-words whitespace-pre-wrap",
                      isOwn
                        ? "bg-gradient-to-br from-[#9bc53d] to-[#9bc53d]/90 text-white rounded-br-md"
                        : "bg-card border border-border/50 text-foreground rounded-bl-md"
                    )}
                  >
                    <p className="leading-relaxed">{msg.message}</p>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground px-2">
                    <span>{formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}</span>
                    {isOwn && (
                      <span className="flex items-center gap-0.5">
                        {msg.status === "read" ? (
                          <CheckCheck className="h-3 w-3 text-[#9bc53d]" />
                        ) : msg.status === "delivered" ? (
                          <CheckCheck className="h-3 w-3" />
                        ) : (
                          <Check className="h-3 w-3" />
                        )}
                        <span className="capitalize">{msg.status === "read" ? "Read" : msg.status}</span>
                      </span>
                    )}
                  </div>
                </div>
                {isOwn && (
                  <Avatar className="h-6 w-6 mt-1 flex-shrink-0">
                    <AvatarFallback className="bg-[#f3fadf] text-[#9bc53d] text-[10px]">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messageEndRef} className="h-2" />
      </div>
    );
  };

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        <ChatDockLauncher
          label="Agent Chat"
          icon={<MessageCircle className="h-5 w-5 text-white" />}
          onClick={() => setIsOpen(true)}
          hidden={isOpen}
          backgroundColor="#9bc53d"
          textColor="#ffffff"
          borderColor="#7ea02f"
          iconBackgroundColor="#9bc53d"
        />
      </div>

      {/* Chat Window */}
      {isOpen && (
        <Card
          className={cn(
            "fixed right-6 z-50 flex flex-col border shadow-2xl transition-all duration-300",
            "bg-card/95 backdrop-blur-sm",
            "animate-in slide-in-from-bottom-4 fade-in-0",
            isExpanded 
              ? "bottom-8 h-[700px] w-[650px]" 
              : "bottom-24 h-[600px] w-[480px]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b bg-gradient-to-r from-[#f9ffe4] to-transparent px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-[#f9ffe4] p-2">
                <MessageCircle className="h-5 w-5 text-[#9bc53d]" />
              </div>
              <div>
                <p className="text-base font-semibold text-foreground">Agent Chat</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  {selectedAgent ? (
                    <>
                      <span className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        selectedAgent.isOnline ? "bg-emerald-500" : "bg-gray-400"
                      )} />
                      {selectedAgent.isOnline ? "Active" : "Offline"}
                    </>
                  ) : (
                    "Talk to your teammates"
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 hover:bg-muted"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Agent List */}
            <div className="w-52 border-r border-[#E1E8F5] bg-white flex flex-col">
              {/* Search */}
              <div className="p-3 border-b bg-[#f9ffe8]">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9bc53d]" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search agents..."
                    className="h-9 pl-8 text-xs border-[#9bc53d]/30 focus-visible:ring-[#9bc53d]/25 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              {/* Agent List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {agentsError && (
                  <div className="text-xs text-destructive p-2 bg-destructive/10 rounded-lg">
                    {agentsError}
                  </div>
                )}
                {isLoadingAgents && (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                )}
                {!isLoadingAgents && !agentsError && filteredAgents.length === 0 && (
                  <div className="py-8 text-center">
                    <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                    <p className="text-xs text-muted-foreground">No agents found</p>
                  </div>
                )}
                {!isLoadingAgents && !agentsError && filteredAgents.map(renderAgent)}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex flex-1 flex-col bg-gradient-to-b from-white to-[#f9ffe7]">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzkwYTJlYSIgc3Ryb2tlLXdpZHRoPSIwLjQiIG9wYWNpdHk9IjAuMDIiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]">
                <div className="max-w-full">{renderMessages()}</div>
              </div>

              {/* Input Area */}
              <div className="border-t bg-white p-4 space-y-3">
                <div className="relative">
                  <Textarea
                    value={messageInput}
                    onChange={(event) => setMessageInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={selectedAgent ? `Message ${selectedAgent.name.split(" ")[0]}...` : "Select an agent to start chatting"}
                    rows={3}
                    disabled={!selectedAgent || isSending}
                    className={cn(
                      "resize-none text-sm border-[#9bc53d]/25 focus-visible:ring-[#9bc53d]/20",
                      "min-h-[60px] max-h-[120px]"
                    )}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">
                    Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> to send • 
                    <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] ml-1">Shift + Enter</kbd> for newline
                  </p>
                  <Button
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={!selectedAgent || !messageInput.trim() || isSending}
                    className="bg-[#9bc53d] hover:bg-[#86a831] text-white shadow-sm"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-3.5 w-3.5" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default AgentChatDock;
